import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, MessageSquare, ArrowLeft, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { ReportDialog } from "@/components/ReportDialog";

export default function ThreadDetail() {
  const { id } = useParams();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = () => {
    api.get(`/threads/${id}`).then(({ data }) => setThread(data));
    api.get(`/threads/${id}/replies`).then(({ data }) => setReplies(data));
  };

  useEffect(() => { load(); }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return navigate("/login");
    if (!body.trim()) return;
    setPosting(true);
    try {
      await api.post(`/threads/${id}/replies`, { body });
      setBody("");
      load();
      toast.success("Reply posted");
    } catch (e) {
      toast.error("Couldn't post reply");
    } finally {
      setPosting(false);
    }
  };

  const upvote = async () => {
    if (!user) return navigate("/login");
    try {
      await api.post(`/threads/${id}/upvote`);
      load();
    } catch (e) {
      console.error("upvote failed", e);
      toast.error("Couldn't upvote");
    }
  };

  if (!thread) return <div className="max-w-4xl mx-auto px-4 py-16 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in" data-testid="thread-detail">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6" data-testid="btn-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <Card className="bg-card">
        <CardContent className="p-6 md:p-8">
          <Badge variant="outline" className="border-accent/40 text-accent bg-accent/5">{thread.category}</Badge>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl mt-3 leading-tight">{thread.title}</h1>
          <div className="flex items-center gap-3 mt-4 text-sm text-muted-foreground">
            <Link to={`/profile/${thread.author_id}`} className="flex items-center gap-2 hover:text-foreground" data-testid="link-thread-author">
              <Avatar className="w-8 h-8"><AvatarImage src={thread.author_picture} /><AvatarFallback>{thread.author_name?.[0]}</AvatarFallback></Avatar>
              <span className="font-medium text-foreground">{thread.author_name}</span>
            </Link>
            <span>·</span>
            <span>{formatDistanceToNow(new Date(thread.created_at), { addSuffix: true })}</span>
          </div>
          <p className="mt-6 whitespace-pre-wrap text-base leading-relaxed">{thread.body}</p>
          {thread.image_paths?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
              {thread.image_paths.map((p, i) => (
                <img key={i} src={fileUrl(p)} alt="" className="w-full aspect-square object-cover rounded-md" />
              ))}
            </div>
          )}
          <div className="flex items-center gap-4 mt-6 pt-6 border-t">
            <Button variant="outline" onClick={upvote} data-testid="btn-upvote-thread" className="border-primary/30 hover:bg-primary/5">
              <ArrowUp className="w-4 h-4 mr-2" /> {thread.upvotes} Upvote
            </Button>
            <span className="text-sm text-muted-foreground inline-flex items-center gap-1">
              <MessageSquare className="w-4 h-4" /> {thread.reply_count} replies
            </span>
            <div className="ml-auto">
              <ReportDialog targetType="thread" targetId={thread.thread_id} testId="btn-report-thread" />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mt-10">
        <h2 className="font-display font-bold text-xl mb-4">Replies</h2>
        <div className="space-y-3" data-testid="reply-list">
          {replies.map((r) => (
            <Card key={r.reply_id} className="bg-card">
              <CardContent className="p-5">
                <div className="flex gap-3">
                  <Avatar className="w-9 h-9"><AvatarImage src={r.author_picture} /><AvatarFallback>{r.author_name?.[0]}</AvatarFallback></Avatar>
                  <div className="flex-1">
                    <div className="text-sm">
                      <Link to={`/profile/${r.author_id}`} className="font-semibold hover:text-primary">{r.author_name}</Link>
                      <span className="text-muted-foreground"> · {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>
                    </div>
                    <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed">{r.body}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6" data-testid="reply-form">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={user ? "Say something useful…" : "Sign in to reply"}
            className="min-h-[100px] bg-card"
            disabled={!user}
            data-testid="reply-textarea"
          />
          <div className="mt-3 flex justify-end">
            <Button type="submit" disabled={!user || posting || !body.trim()} className="bg-primary hover:bg-primary/90" data-testid="btn-post-reply">
              {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Post reply
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
